import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface AnalyticsData {
  period: string;
  couponsUsed: number;
  revenue: number;
  savings: number;
  conversionRate: number;
}

interface TopCoupon {
  code: string;
  uses: number;
  revenue: number;
  conversionRate: number;
}

const MOCK_ANALYTICS: AnalyticsData[] = [
  { period: "Ene", couponsUsed: 145, revenue: 12500, savings: 2800, conversionRate: 68 },
  { period: "Feb", couponsUsed: 189, revenue: 15200, savings: 3400, conversionRate: 72 },
  { period: "Mar", couponsUsed: 234, revenue: 18900, savings: 4200, conversionRate: 75 },
  { period: "Abr", couponsUsed: 198, revenue: 16400, savings: 3800, conversionRate: 71 },
  { period: "May", couponsUsed: 267, revenue: 21500, savings: 4900, conversionRate: 78 },
  { period: "Jun", couponsUsed: 312, revenue: 24800, savings: 5600, conversionRate: 82 },
];

const TOP_COUPONS: TopCoupon[] = [
  { code: "WELCOME20", uses: 456, revenue: 45600, conversionRate: 85 },
  { code: "SUMMER25", uses: 312, revenue: 31200, conversionRate: 78 },
  { code: "VIP15", uses: 289, revenue: 28900, conversionRate: 92 },
  { code: "FLASH50", uses: 198, revenue: 19800, conversionRate: 65 },
  { code: "SAVE10", uses: 167, revenue: 16700, conversionRate: 71 },
];

export default function AnalyticsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [dateRange, setDateRange] = useState("6months");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
  };

  const totalCouponsUsed = MOCK_ANALYTICS.reduce((acc, d) => acc + d.couponsUsed, 0);
  const totalRevenue = MOCK_ANALYTICS.reduce((acc, d) => acc + d.revenue, 0);
  const totalSavings = MOCK_ANALYTICS.reduce((acc, d) => acc + d.savings, 0);
  const avgConversion = Math.round(MOCK_ANALYTICS.reduce((acc, d) => acc + d.conversionRate, 0) / MOCK_ANALYTICS.length);

  const maxCoupons = Math.max(...MOCK_ANALYTICS.map((d) => d.couponsUsed));

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
            {t("posAnalytics.title")}
          </h1>
          <p className={themeClasses.textSecondary}>
            {t("posAnalytics.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
          >
            <option value="7days">{t("posAnalytics.last7Days")}</option>
            <option value="30days">{t("posAnalytics.last30Days")}</option>
            <option value="6months">{t("posAnalytics.last6Months")}</option>
            <option value="1year">{t("posAnalytics.lastYear")}</option>
          </select>
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
            <i className="ri-download-line"></i>
            {t("posAnalytics.export")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posAnalytics.couponsUsed")}</span>
            <i className="ri-coupon-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{totalCouponsUsed.toLocaleString()}</p>
          <p className="text-sm text-emerald-500 flex items-center gap-1 mt-1">
            <i className="ri-arrow-up-line"></i>
            +12.5% vs período anterior
          </p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posAnalytics.revenueGenerated")}</span>
            <i className="ri-money-dollar-circle-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>${totalRevenue.toLocaleString()}</p>
          <p className="text-sm text-emerald-500 flex items-center gap-1 mt-1">
            <i className="ri-arrow-up-line"></i>
            +8.3% vs período anterior
          </p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posAnalytics.customerSavings")}</span>
            <i className="ri-gift-line text-pink-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>${totalSavings.toLocaleString()}</p>
          <p className="text-sm text-emerald-500 flex items-center gap-1 mt-1">
            <i className="ri-arrow-up-line"></i>
            +15.2% vs período anterior
          </p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posAnalytics.conversionRate")}</span>
            <i className="ri-pie-chart-line text-purple-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{avgConversion}%</p>
          <p className="text-sm text-emerald-500 flex items-center gap-1 mt-1">
            <i className="ri-arrow-up-line"></i>
            +3.1% vs período anterior
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`lg:col-span-2 ${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-6`}>
          <h2 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
            {t("posAnalytics.usageTrend")}
          </h2>
          <div className="h-64 flex items-end gap-4">
            {MOCK_ANALYTICS.map((data, index) => (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full bg-primary-500 rounded-t-lg transition-all hover:bg-[#65eb3d]"
                  style={{ height: `${(data.couponsUsed / maxCoupons) * 100}%` }}
                ></div>
                <span className={`text-sm ${themeClasses.textSecondary}`}>{data.period}</span>
              </div>
            ))}
          </div>
          <div className={`mt-4 pt-4 border-t ${themeClasses.border} flex justify-center gap-8`}>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary-500"></div>
              <span className={`text-sm ${themeClasses.textSecondary}`}>{t("posAnalytics.couponsUsed")}</span>
            </div>
          </div>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-6`}>
          <h2 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
            {t("posAnalytics.topCoupons")}
          </h2>
          <div className="space-y-4">
            {TOP_COUPONS.map((coupon, index) => (
              <div key={coupon.code} className="flex items-center gap-3">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  index === 0 ? "bg-amber-500 text-white" :
                  index === 1 ? "bg-gray-400 text-white" :
                  index === 2 ? "bg-amber-700 text-white" :
                  `${themeClasses.cardBg} ${themeClasses.text}`
                }`}>
                  {index + 1}
                </span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className={`font-mono font-medium ${themeClasses.text}`}>{coupon.code}</span>
                    <span className={`text-sm ${themeClasses.textSecondary}`}>{coupon.uses} usos</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-1.5 mt-1">
                    <div
                      className="bg-primary-500 h-1.5 rounded-full"
                      style={{ width: `${coupon.conversionRate}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`mt-6 ${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-6`}>
        <h2 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
          {t("posAnalytics.performanceByChannel")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className={`${themeClasses.cardBg} rounded-xl p-4`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <i className="ri-store-line text-blue-500 text-xl"></i>
              </div>
              <div>
                <p className={`font-medium ${themeClasses.text}`}>POS</p>
                <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posAnalytics.pointOfSale")}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posAnalytics.redemptions")}</p>
                <p className={`text-xl font-bold ${themeClasses.text}`}>845</p>
              </div>
              <div>
                <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posAnalytics.revenue")}</p>
                <p className={`text-xl font-bold ${themeClasses.text}`}>$67,200</p>
              </div>
            </div>
          </div>

          <div className={`${themeClasses.cardBg} rounded-xl p-4`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <i className="ri-global-line text-violet-500 text-xl"></i>
              </div>
              <div>
                <p className={`font-medium ${themeClasses.text}`}>WEB</p>
                <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posAnalytics.onlineStore")}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posAnalytics.redemptions")}</p>
                <p className={`text-xl font-bold ${themeClasses.text}`}>500</p>
              </div>
              <div>
                <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posAnalytics.revenue")}</p>
                <p className={`text-xl font-bold ${themeClasses.text}`}>$42,100</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
